import React from 'react';
import classNames from 'classnames';
import { Link, withRouter } from 'react-router-dom';
import { SectionProps } from '../../utils/SectionProps';
import Button from '../elements/Button';
import '../../assets/scss/settings/sections/_pricing.scss';
import { fetchPlans } from '../../api/plans';
import { fetchLandingContent } from '../../api/landing';
import { plans as fallbackPlans } from '../../utils/pricingPlans';
import api from '../../api/http';
import { API_ENDPOINTS } from '../../api/endpoints';
import { withLanguage } from '../../context/LanguageContext';

const propTypes = {
  ...SectionProps.types
};

const defaultProps = {
  ...SectionProps.defaults
};

// Plans will be fetched dynamically

class PricingGrid extends React.Component {
  constructor(props) {
    super(props);
    this.modeCardsRef = React.createRef();
    this.planCardsRef = React.createRef();
  }

  state = {
    showCycleModal: false,
    selectedPlan: null,
    includeBoxAddon: false,
    plans: [],
    loading: true,
    activeModeIndex: 1, // Default to Premium
    activePlanIndex: 0, // Default to 1st plan
    pricingModes: null,
    pricingBullets: null,
    addon: null,
    contactEmail: null,
    license: null,
    licenseLoaded: false
  };

  async componentDidMount() {
    if (this.isLoggedIn()) {
      try {
        const me = await api.get(API_ENDPOINTS.ME);
        this.setState({ license: me.data?.data?.license || null, licenseLoaded: true });
      } catch (error) {
        this.setState({ license: null, licenseLoaded: true });
      }
    } else {
      this.setState({ licenseLoaded: true });
    }

    try {
      const contentRes = await fetchLandingContent();
      if (contentRes?.success) {
        if (contentRes.data?.pricing_modes) {
          this.setState({ pricingModes: contentRes.data.pricing_modes });
        }
        if (contentRes.data?.pricing_bullets) {
          this.setState({ pricingBullets: contentRes.data.pricing_bullets });
        }
        if (contentRes.data?.addon) {
          this.setState({ addon: contentRes.data.addon });
        }
        if (contentRes.data?.contactEmail) {
          this.setState({ contactEmail: contentRes.data.contactEmail });
        }
      }
    } catch (error) {
      console.error("Failed to fetch landing content in Pricing", error);
    }

    try {
      const res = await fetchPlans();
      if (res?.data?.data && res.data.data.length > 0) {
        const englishPlanCopy = {
          plan_multi_s: { title: 'Plan S', feature: 'Essential plan for small teams' },
          plan_multi_m: { title: 'Plan M', feature: 'Designed for growing teams' },
          plan_multi_l: { title: 'Plan L', feature: 'Enterprise plan for large-scale usage' }
        };
        const mappedPlans = res.data.data.map(p => ({
          id: p.planId.replace('plan_', ''),
          signupPlanId: p.planId.replace('plan_', ''),
          backendPlanId: p.planId,
          type: 'マルチテナント',
          title: p.planName,
          titleEn: p.planNameEn || englishPlanCopy[p.planId]?.title || p.planName,
          priceAnnual: Number(p.monthlyPriceAnnual).toLocaleString() + '円',
          priceMonthly: Number(p.monthlyPriceMonthly).toLocaleString() + '円',
          creditsGranted: Number(p.grantedCredits).toLocaleString(),
          creditsLimit: Number(p.creditLimit).toLocaleString(),
          // Numeric values for comparisons ("2,400" would become NaN).
          creditLimitValue: Number(p.creditLimit),
          upcomingChange: p.upcomingChange || null,
          features: p.description ? [p.description] : [],
          featuresEn: p.descriptionEn
            ? [p.descriptionEn]
            : (englishPlanCopy[p.planId]?.feature ? [englishPlanCopy[p.planId].feature] : [])
        }));
        this.setState({ plans: mappedPlans, loading: false }, () => {
          setTimeout(() => {
            this.scrollToCard(this.modeCardsRef.current, 1);
            this.scrollToCard(this.planCardsRef.current, 0);
          }, 150);
        });
      } else {
        this.setState({ plans: fallbackPlans, loading: false });
      }
    } catch (error) {
      console.error("Failed to fetch plans", error);
      this.setState({ plans: fallbackPlans, loading: false });
    }
  }

  scrollToCard = (container, index) => {
    if (!container || window.innerWidth > 767) return;
    const cards = Array.from(container.children).filter(
      c => c.classList.contains('ocr-mode-card') || c.classList.contains('pricing-card')
    );
    if (cards && cards[index]) {
      const card = cards[index];
      const scrollLeft = card.offsetLeft - (container.offsetWidth - card.offsetWidth) / 2;
      container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
    }
  };

  handleScroll = (e, type) => {
    if (window.innerWidth > 767) return;
    const container = e.target;
    const scrollLeft = container.scrollLeft;
    const centerPoint = scrollLeft + container.offsetWidth / 2;
    
    let activeIndex = 0;
    let minDistance = Infinity;
    
    Array.from(container.children).forEach((child, index) => {
      if (!child.classList.contains('ocr-mode-card') && !child.classList.contains('pricing-card')) return;
      const childCenter = child.offsetLeft + child.offsetWidth / 2;
      const distance = Math.abs(childCenter - centerPoint);
      if (distance < minDistance) {
        minDistance = distance;
        activeIndex = index;
      }
    });

    if (type === 'mode' && this.state.activeModeIndex !== activeIndex) {
      this.setState({ activeModeIndex: activeIndex });
    } else if (type === 'plan' && this.state.activePlanIndex !== activeIndex) {
      this.setState({ activePlanIndex: activeIndex });
    }
  };

  isLoggedIn() {
    const token =
      localStorage.getItem('accessToken') ||
      localStorage.getItem('token') ||
      sessionStorage.getItem('accessToken') ||
      sessionStorage.getItem('token');

    return Boolean(token);
  }

  openCycleModal = (plan) => {
    this.setState({
      showCycleModal: true,
      selectedPlan: plan,
      includeBoxAddon: false
    });
  };

  closeCycleModal = () => {
    this.setState({
      showCycleModal: false,
      selectedPlan: null,
      includeBoxAddon: false
    });
  };

  handleCycleSelect = (cycle) => {
    const { selectedPlan, includeBoxAddon, license } = this.state;
    if (!selectedPlan) return;

    const currentPlan = this.state.plans.find((plan) => plan.backendPlanId === license?.planId);
    const hasActivePlan = license?.status === 'active' && currentPlan;
    const targetCredits = Number(selectedPlan.creditLimitValue || 0);
    const currentCredits = Number(currentPlan?.creditLimitValue || 0);
    const isUpgrade = Boolean(hasActivePlan && targetCredits > currentCredits);
    if (isUpgrade && cycle !== license.billingCycle) return;

    const query = new URLSearchParams({
      planId: selectedPlan.backendPlanId,
      cycle,
      ...(isUpgrade ? { purchase: 'upgrade' } : {}),
      ...(includeBoxAddon ? { boxAddon: 'true' } : {})
    });

    this.props.history.push(`/settings/invoice?${query.toString()}`);

    this.closeCycleModal();
  };

  getPlanAction(plan) {
    const { text } = this.props;
    const loggedIn = this.isLoggedIn();

    if (loggedIn) {
      return {
        label: text('このプランに変更', 'Change to this plan'),
        onClick: () => this.openCycleModal(plan)
      };
    }

    return {
      label: text('無料トライアルを開始', 'Start free trial'),
      to: `/signup?planId=${encodeURIComponent(plan.signupPlanId)}`
    };
  }

  renderCard(plan) {
    const { text, isEnglish } = this.props;
    const action = this.getPlanAction(plan);

    return (
      <article
        key={plan.id}
        className={`pricing-card card--${plan.id}`}
        aria-labelledby={`title-${plan.id}`}
      >
        <div className="card-head">
          <div className="plan-type">{text(plan.type, 'Multi-tenant')}</div>

          <h3 id={`title-${plan.id}`} className="plan-title">
            {isEnglish ? (plan.titleEn || plan.title) : plan.title}
          </h3>

          <div className="plan-price" aria-hidden={false}>
            {plan.oneTime ? (
              <div className="one-time">
                <strong>導入一時金：</strong>
                {plan.oneTime}
              </div>
            ) : null}

            <div className="annual">
              <span className="yen">¥</span>
              <span className="amount">
                {String(plan.priceAnnual).replace(/¥|円/g, '')}
              </span>
              <span className="suffix">{text(' /月（年次請求換算）', ' /month (annual billing)')}</span>
            </div>

            <div className="sub">
              <div>
                <strong>{text('月次請求：', 'Monthly billing: ')}</strong>
                {isEnglish ? `¥${String(plan.priceMonthly).replace(/¥|円/g, '')}` : plan.priceMonthly}
              </div>
            </div>
          </div>
        </div>

        <div className="card-body">
          <div className="features-title">{text('含まれる内容', 'Included')}</div>

          <ul className="features">
            {(isEnglish ? (plan.featuresEn || plan.features) : plan.features).map((feature, index) => (
              <li key={`${plan.id}-feature-${index}`}>{feature}</li>
            ))}

            <li>
              <strong>{text('付与クレジット：', 'Granted credits: ')}</strong>
              {plan.creditsGranted}
            </li>

            <li>
              <strong>{text('プール上限：', 'Pool limit: ')}</strong>
              {plan.creditsLimit}
            </li>

            {plan.upcomingChange && (
              <li style={{ color: '#b45309' }}>
                {text(
                  `${new Date(plan.upcomingChange.effectiveFrom).toLocaleDateString('ja-JP', { timeZone: 'Asia/Tokyo' })}より料金改定: 月額${Number(plan.upcomingChange.monthlyPriceAnnual).toLocaleString()}円（年次）／${Number(plan.upcomingChange.monthlyPriceMonthly).toLocaleString()}円（月次）、付与${Number(plan.upcomingChange.grantedCredits).toLocaleString()}クレジット`,
                  `Price change from ${new Date(plan.upcomingChange.effectiveFrom).toLocaleDateString('en-US', { timeZone: 'Asia/Tokyo' })}: ¥${Number(plan.upcomingChange.monthlyPriceAnnual).toLocaleString()}/month (annual), ¥${Number(plan.upcomingChange.monthlyPriceMonthly).toLocaleString()} (monthly), ${Number(plan.upcomingChange.grantedCredits).toLocaleString()} credits`
                )}
              </li>
            )}
          </ul>
        </div>

        <div className="card-footer">
          {action.to ? (
            <Link to={action.to} style={{ width: '100%', display: 'block' }}>
              <Button color="primary" wide>
                {action.label}
              </Button>
            </Link>
          ) : (
            <div style={{ width: '100%', display: 'block' }}>
              <Button color="primary" wide onClick={action.onClick}>
                {action.label}
              </Button>
            </div>
          )}
        </div>
      </article>
    );
  }

  renderCycleModal() {
    const { showCycleModal, selectedPlan, license } = this.state;
    const { isEnglish, text } = this.props;
    if (!showCycleModal || !selectedPlan) return null;

    const currentPlan = this.state.plans.find((plan) => plan.backendPlanId === license?.planId);
    const isUpgrade = license?.status === 'active' && currentPlan &&
      Number(selectedPlan.creditLimitValue || 0) >
      Number(currentPlan.creditLimitValue || 0);
    const annualDisabled = !this.state.licenseLoaded || (isUpgrade && license?.billingCycle !== 'annual');
    const monthlyDisabled = !this.state.licenseLoaded || (isUpgrade && license?.billingCycle !== 'monthly');
    const displayPrice = (price) => String(price || '').replace(/円/g, '').trim();

    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}
        onClick={this.closeCycleModal}
      >
        <div
          style={{
            width: '100%',
            maxWidth: '460px',
            background: '#ffffff',
            borderRadius: '16px',
            padding: '28px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.25)'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <h3
            style={{
              marginTop: 0,
              marginBottom: '12px',
              fontSize: '28px',
              color: '#141d2f'
            }}
          >
            {text('請求サイクルを選択してください', 'Choose a billing cycle')}
          </h3>

          <p
            style={{
              marginTop: 0,
              marginBottom: '8px',
              color: '#4a5568',
              fontSize: '16px',
              lineHeight: 1.6,
              fontWeight: 700
            }}
          >
            {isEnglish ? (selectedPlan.titleEn || selectedPlan.title) : selectedPlan.title}
          </p>

          <p
            style={{
              marginTop: 0,
              marginBottom: '24px',
              color: '#4a5568',
              fontSize: '15px',
              lineHeight: 1.6
            }}
          >
            {text('ご希望の請求方法を選択してください。', 'Select your preferred billing cycle.')}
          </p>

          {isUpgrade && (
            <p style={{ marginTop: 0, marginBottom: 18, color: '#4a5568', fontSize: 14, lineHeight: 1.5 }}>
              {text(
                '上位プランへの変更料金は、現在の契約期間の残り日数に応じて計算されます。請求サイクルは現在の契約と同じものを選択してください。',
                'The upgrade charge is prorated for the remaining days in your current term. Keep your current billing cycle.'
              )}
            </p>
          )}

          {!isUpgrade && <label className="box-addon-choice">
            <input
              type="checkbox"
              checked={this.state.includeBoxAddon}
              onChange={(event) => this.setState({ includeBoxAddon: event.target.checked })}
            />
            <span>
              <strong>{text('BOX連携オプションを追加', 'Add BOX integration')}</strong>
              <small>{text('月1ユーザーあたり 500円（税別）。ご入金確認後にBOX接続が有効になります。', '¥500 per user/month, before tax. BOX integration is enabled after payment is confirmed.')}</small>
            </span>
          </label>}

          <div style={{ display: 'grid', gap: '14px' }}>
            <button
              type="button"
              onClick={() => this.handleCycleSelect('annual')}
              disabled={annualDisabled}
              style={{
                width: '100%',
                minHeight: '72px',
                borderRadius: '12px',
                border: '1px solid #cbd5e1',
                background: '#ffffff',
                fontSize: '18px',
                fontWeight: 800,
                cursor: annualDisabled ? 'not-allowed' : 'pointer',
                opacity: annualDisabled ? 0.55 : 1,
                textAlign: 'left',
                padding: '16px 18px'
              }}
            >
              <div>{text('年次請求', 'Annual billing')}</div>
              <div
                style={{
                  marginTop: '6px',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#64748b'
                }}
              >
                {displayPrice(selectedPlan.priceAnnual)} {text('/月（年次請求換算）', '/month (billed annually)')}
              </div>
            </button>

            <button
              type="button"
              onClick={() => this.handleCycleSelect('monthly')}
              disabled={monthlyDisabled}
              style={{
                width: '100%',
                minHeight: '72px',
                borderRadius: '12px',
                border: '1px solid #cbd5e1',
                background: '#ffffff',
                fontSize: '18px',
                fontWeight: 800,
                cursor: monthlyDisabled ? 'not-allowed' : 'pointer',
                opacity: monthlyDisabled ? 0.55 : 1,
                textAlign: 'left',
                padding: '16px 18px'
              }}
            >
              <div>{text('月次請求', 'Monthly billing')}</div>
              <div
                style={{
                  marginTop: '6px',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#64748b'
                }}
              >
                {displayPrice(selectedPlan.priceMonthly)} {text('/月', '/month')}
              </div>
            </button>

            <button
              type="button"
              onClick={this.closeCycleModal}
              style={{
                width: '100%',
                minHeight: '52px',
                borderRadius: '12px',
                border: 'none',
                background: '#e5e7eb',
                color: '#111827',
                fontSize: '16px',
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              {text('キャンセル', 'Cancel')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { text, isEnglish } = this.props;
    const englishBullets = [
      'Credits are granted according to the selected plan after bank payment is confirmed. OCR usage consumes credits, and unused credits can be pooled up to the plan limit.',
      'For PDF documents, one credit is consumed per uploaded page.',
      'For images, page usage is calculated using the A4 aspect ratio.',
      'Each run consumes credits based on its page count. Test and unsuccessful runs are also counted.',
      'OCR stops when all credits are used. Additional credits can be purchased for ¥2,000 per 100 credits.'
    ];
    const englishModeCopy = {
      Standard: { title: 'Standard', speed: 'Faster', accuracy: 'Suitable for most cases', features: ['No confidence data'] },
      Premium: { title: 'Premium', speed: 'Slightly slower', accuracy: 'Highest accuracy', features: ['Includes confidence data'] },
      Fast: { title: 'Fast', speed: 'Fastest', accuracy: 'Suitable for simple documents', features: ['No confidence data'] }
    };
    const {
      className,
      topOuterDivider,
      bottomOuterDivider,
      hasBgColor,
      invertColor,
      ...props
    } = this.props;

    const outerClasses = classNames(
      'pricing-grid section',
      topOuterDivider && 'has-top-divider',
      bottomOuterDivider && 'has-bottom-divider',
      hasBgColor && 'has-bg-color',
      invertColor && 'invert-color',
      className
    );

    return (
      <>
        <section id="pricing" {...props} className={outerClasses}>
          <div className="container">
            <h2 className="pricing-intro__title">
              {text('PITHIAS Extract - 価格とプラン', 'PITHIAS Extract - Pricing and plans')}
            </h2>

            <div className="pricing-intro__desc">
              <ul>
                {isEnglish ? (
                  englishBullets.map((bullet, idx) => <li key={idx}>{bullet}</li>)
                ) : this.state.pricingBullets ? (
                  this.state.pricingBullets.map((bullet, idx) => (
                    <li key={idx}>{bullet}</li>
                  ))
                ) : (
                  <>
                    <li>
                      銀行振込実績に基づき、プランに応じたクレジットを付与します。クレジットはOCR実行により消費され、使い切らない場合上限までプール可能です。
                    </li>
                    <li>
                      PDFの場合、アップロードされた文書の1ページで1クレジット消費します。
                    </li>
                    <li>
                      画像データの場合、A4アスペクト比に基づいて消費するページ数をカウントし、消費クレジットを計算します。
                    </li>
                    <li>
                      実行処理1度につきページ枚数分のクレジットを上限から消化します。ミスやテストもカウントされます。
                    </li>
                    <li>
                      クレジットをすべて消費した場合はOCRが停止しますが、100クレジットあたり2,000円で追加購入可能です。
                    </li>
                  </>
                )}
              </ul>
            </div>

            <div className="mobile-carousel-container">
              <div className="carousel-bullets mobile-only">
                {(this.state.pricingModes || [0, 1]).map((_, idx) => (
                  <button 
                    key={idx} 
                    type="button"
                    className={`carousel-bullet ${this.state.activeModeIndex === idx ? 'is-active' : ''}`}
                    onClick={() => this.scrollToCard(this.modeCardsRef.current, idx)}
                  />
                ))}
              </div>
              <div 
                className="ocr-mode-cards"
                ref={this.modeCardsRef}
                onScroll={(e) => this.handleScroll(e, 'mode')}
              >
                {this.state.pricingModes ? (
                  this.state.pricingModes.map((mode, idx) => {
                    const modeKey = [mode.id, mode.titleEn, mode.title]
                      .find((value) => englishModeCopy[value]);
                    const englishMode = englishModeCopy[modeKey] || {};
                    return (
                    <div key={idx} className={`ocr-mode-card ${mode.id === 'Premium' || mode.title === 'Premium' ? 'premium' : ''}`}>
                      <h4>{isEnglish ? (mode.titleEn || englishMode.title || mode.title) : mode.title}</h4>
                      <ul>
                        <li>💰 {mode.credits}</li>
                        <li>⚡ {isEnglish ? (mode.speedEn || englishMode.speed || mode.speed) : mode.speed}</li>
                        <li>🎯 {isEnglish ? (mode.accuracyEn || englishMode.accuracy || mode.accuracy) : mode.accuracy}</li>
                        {(isEnglish ? (mode.featuresEn || englishMode.features || mode.features || []) : (mode.features || [])).map((f, i) => <li key={i}>✓ {f}</li>)}
                      </ul>
                    </div>
                    );
                  })
                ) : (
                  <>
                    <div className="ocr-mode-card">
                      <h4>Standard</h4>
                      <ul>
                        <li>💰 2 credits</li>
                        <li>⚡ {text('やや早い', 'Faster')}</li>
                        <li>🎯 {text('多くのケースで十分', 'Suitable for most cases')}</li>
                        <li>✓ {text('信頼度データなし', 'No confidence data')}</li>
                      </ul>
                    </div>

                    <div className="ocr-mode-card premium">
                      <h4>Premium</h4>
                      <ul>
                        <li>💰 5 credits</li>
                        <li>⚡ {text('やや遅い', 'Slightly slower')}</li>
                        <li>🎯 {text('最高レベル', 'Highest accuracy')}</li>
                        <li>✓ {text('信頼度データ付与', 'Includes confidence data')}</li>
                      </ul>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="mobile-carousel-container">
              {!this.state.loading && this.state.plans.length > 0 && (
                <div className="carousel-bullets mobile-only">
                  {this.state.plans.map((_, idx) => (
                    <button 
                      key={idx} 
                      type="button"
                      className={`carousel-bullet ${this.state.activePlanIndex === idx ? 'is-active' : ''}`}
                      onClick={() => this.scrollToCard(this.planCardsRef.current, idx)}
                    />
                  ))}
                </div>
              )}
              <div 
                className="grid-wrap" 
                aria-live="polite"
                ref={this.planCardsRef}
                onScroll={(e) => this.handleScroll(e, 'plan')}
              >
                {this.state.loading ? (
                  <div style={{ textAlign: 'center', width: '100%', padding: '20px' }}>Loading...</div>
                ) : (
                  this.state.plans.map((plan) => this.renderCard(plan))
                )}
              </div>
            </div>

            {/* Add-on: external storage integration */}
            <div className="addon-section">
              <h3 className="addon-heading">{text('追加オプション', 'Additional options')}</h3>

              <div className="addon-card">
                <span className="addon-tag">{this.state.addon?.tag || 'NEW'}</span>

                <div className="addon-main">
                  <div className="addon-info">
                    <h4 className="addon-name">{isEnglish ? 'Box integration add-on' : (this.state.addon?.name || 'BOX連携オプション')}</h4>
                    <p className="addon-lead">
                      {isEnglish ? 'Automatically run OCR by saving images to Box.' : (this.state.addon?.lead || 'BOX に画像を保存するだけで、OCR抽出を自動化します。')}
                    </p>
                    <ul className="addon-features">
                      {!isEnglish && this.state.addon?.features?.length > 0 ? (
                        this.state.addon.features.map((f, i) => <li key={i}>{f}</li>)
                      ) : (
                        <>
                          <li>{text('連携すると、BOX側に専用フォルダを自動作成', 'Automatically creates a dedicated folder in Box')}</li>
                          <li>{text('対象フォルダに画像を保存するだけで自動でOCR抽出', 'Runs OCR automatically when images are saved to the folder')}</li>
                          <li>{text('抽出結果は専用フォルダに自動で蓄積', 'Stores extraction results in a dedicated folder')}</li>
                        </>
                      )}
                    </ul>
                  </div>

                  <div className="addon-pricebox">
                    <div className="addon-price">
                      <span className="yen">¥</span>
                      <span className="amount">{this.state.addon?.price || '500'}</span>
                      <span className="suffix">{text(' /月（ユーザーあたり）', ' /month per user')}</span>
                    </div>
                    <p className="addon-note">{isEnglish ? 'Available with every multi-tenant plan' : (this.state.addon?.note || 'マルチテナントの各プランに追加できます')}</p>
                    {this.isLoggedIn() && (
                      <button
                        type="button"
                        className="addon-purchase-button"
                        onClick={() => this.props.history.push('/settings/invoice?purchase=box')}
                      >
                        {text('BOX連携だけを追加', 'Add Box integration only')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Single-tenant: contact sales */}
            <p className="pricing-contact-note">
              {text('シングルテナント（専用環境）若しくはプランL以上をご希望の場合は、', 'For a single-tenant environment or a plan above L, contact ')}
              <a href={`mailto:${this.state.contactEmail || 'support@pithias.com'}`}>
                {this.state.contactEmail || 'support@pithias.com'}
              </a>
              {text('までお問い合わせください。', '.')}
            </p>
          </div>
        </section>

        {this.renderCycleModal()}
      </>
    );
  }
}

PricingGrid.propTypes = propTypes;
PricingGrid.defaultProps = defaultProps;

export default withRouter(withLanguage(PricingGrid));
